import { Button, EmptyState, IconButton, List, ListRow, SearchInput, Sheet } from '@gramas/ui'
import { useEffect, useState } from 'react'
import { searchFoodsEverywhere } from '../../api/foods.js'
import type { LocalFood } from '../../db/dexie.js'
import { foodDisplayName } from '../../db/repo.js'
import { PersonaSpinner } from '../../glitime/PersonaSpinner.js'
import { sassLine } from '../../sass/engine.js'
import { useSassOnce } from '../../sass/useSassOnce.js'
import { BarcodeScanSheet } from './BarcodeScanSheet.js'
import { ManualFoodSheet } from './ManualFoodSheet.js'

/**
 * Finding a food by hand, wherever the app guessed and got it wrong.
 *
 * Every automatic match — a recipe line, a photograph, a spoken word — is a
 * guess about which catalogue row a person meant, and the guess is sometimes
 * wrong in a way no ranking will fix, because the catalogue simply does not
 * hold the thing. What makes that survivable is a way out: search yourself, and
 * if the catalogue really has nothing, add it. Without those two the screen is
 * a dead end, which is what "no way of manually searching" meant.
 *
 * Searches the server, not just the phone. The local slice holds what has been
 * eaten before, which is the wrong half of the catalogue when the whole problem
 * is that this ingredient has never been logged.
 */
export function FoodPickerSheet({
  open,
  onOpenChange,
  onPick,
  title = 'Rasti produktą',
  initialQuery = '',
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPick: (food: LocalFood) => void
  title?: string
  /** Prefilled with the word that failed to match, since that is the search. */
  initialQuery?: string
}) {
  const [query, setQuery] = useState(initialQuery)
  const [results, setResults] = useState<LocalFood[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [creating, setCreating] = useState(false)
  const [scanning, setScanning] = useState(false)

  useEffect(() => {
    if (open) {
      setQuery(initialQuery)
      setResults(null)
    }
  }, [open, initialQuery])

  useEffect(() => {
    if (!open || query.trim().length < 2) {
      setResults(null)
      setSearching(false)
      return
    }
    let cancelled = false
    setSearching(true)
    // Local first so the list is never empty while the request is in flight,
    // then both halves ranked together once the server answers.
    const handle = window.setTimeout(async () => {
      const merged = await searchFoodsEverywhere(query, 30, (local) => {
        if (!cancelled) setResults(local)
      })
      if (!cancelled) {
        setResults(merged)
        setSearching(false)
      }
    }, 200)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [open, query])

  const shown = results ?? []
  const noResults = !searching && shown.length === 0 && query.trim().length >= 2
  const emptyTitle = useSassOnce(
    noResults ? 'search' : null,
    () => sassLine('empty_list', { surface: 'search' }) ?? 'Kataloge nieko nerasta',
  )

  return (
    <Sheet open={open} onOpenChange={onOpenChange} title={title} size="full">
      <div className="sticky top-0 z-10 flex flex-col gap-2 bg-surface px-4 py-3">
        <div className="flex gap-2">
          <SearchInput
            value={query}
            onValueChange={setQuery}
            label="Ieškoti produkto"
            placeholder="Ieškoti produkto…"
            tone="bg"
            // The sheet exists only to run this search, so it opens focused.
            ref={(el) => el?.focus()}
          />
          {/* The packet is in your hand while you cook, and it names the exact
              product a typed search can only approximate. */}
          <IconButton label="Nuskaityti brūkšninį kodą" onClick={() => setScanning(true)}>
            <svg viewBox="0 0 20 20" className="size-5" fill="none" aria-hidden="true">
              <path
                d="M3 6V4.5A1.5 1.5 0 014.5 3H6M14 3h1.5A1.5 1.5 0 0117 4.5V6M17 14v1.5a1.5 1.5 0 01-1.5 1.5H14M6 17H4.5A1.5 1.5 0 013 15.5V14"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
              <path d="M6 7v6M8.5 7v6M11 7v6M14 7v6" stroke="currentColor" strokeWidth="1.4" />
            </svg>
          </IconButton>
          <Button size="sm" onClick={() => setCreating(true)}>
            Naujas
          </Button>
        </div>
      </div>

      {searching && shown.length === 0 && (
        <div className="flex justify-center py-8">
          <PersonaSpinner surface="search" />
        </div>
      )}

      {!searching && shown.length === 0 && (
        <EmptyState
          title={query.trim().length < 2 ? 'Įveskite pavadinimą' : (emptyTitle ?? '')}
          hint={
            query.trim().length < 2
              ? 'Ieškoma ir telefone, ir serveryje.'
              : 'Jei tokio produkto kataloge nėra — sukurkite jį mygtuku „Naujas“ ir įrašykite vertes iš pakuotės.'
          }
        />
      )}

      <List>
        {shown.map((food, i) => (
          <ListRow
            key={food.id}
            last={i === shown.length - 1}
            title={foodDisplayName(food)}
            subtitle={`B ${Math.round(food.protein)} · A ${Math.round(food.carbs)} · R ${Math.round(food.fat)}`}
            value={`${Math.round(food.kcal)}`}
            meta={`kcal/100 ${food.unit}`}
            onActivate={() => {
              onPick(food)
              onOpenChange(false)
            }}
          />
        ))}
      </List>
      <div className="h-4" />

      <ManualFoodSheet
        open={creating}
        onOpenChange={setCreating}
        onSaved={(food) => {
          setCreating(false)
          onPick(food)
          onOpenChange(false)
        }}
      />

      <BarcodeScanSheet
        open={scanning}
        onOpenChange={setScanning}
        onPick={(food) => {
          setScanning(false)
          onPick(food)
          onOpenChange(false)
        }}
      />
    </Sheet>
  )
}
